import React, { useEffect, useId, useRef, useState } from "react";
import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import PlanningWriteActivityNotice from "./PlanningWriteActivityNotice.jsx";
import { createPortal } from "react-dom";
import { ShieldCheck } from "lucide-react";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { renderInlineNoBreakIdentifiers } from "../utils/inlineMessageFormatting.js";
import { handleBillingDialogKeyDown } from "../utils/billingDialogAccessibility.js";
import { REORDERED_PROTECTION_ACCENT_COLOR, REORDERED_PROTECTION_TONE } from "../utils/protectionVisualState.js";

const toneVariants = {
  neutral: {
    badgeClassName: "border border-slate-300 bg-white text-slate-700 shadow-none",
    accentBarClassName: "bg-accent",
    iconShellClassName: "border border-slate-300 bg-white text-accent-strong shadow-none",
    confirmButtonClassName: "border-accent bg-accent text-white shadow-none hover:border-accent-strong hover:bg-accent-strong focus-visible:ring-accent/35",
    impactDotClassName: "bg-accent",
    warningIconClassName: "bg-accent text-white",
    warningMessageClassName: "border border-slate-300 bg-white text-slate-700 shadow-none",
  },
  warning: {
    badgeClassName: "border border-[var(--color-border-warning-emphasis)] bg-amber text-white shadow-none",
    accentBarClassName: "bg-amber",
    iconShellClassName: "border border-[var(--color-border-warning-subtle)] bg-amber-soft text-warning-ink shadow-none",
    confirmButtonClassName: "border-[var(--color-border-warning-emphasis)] bg-amber text-white shadow-none hover:bg-[var(--color-alert-warning-ink)] focus-visible:ring-amber/35",
    impactDotClassName: "bg-amber",
    warningIconClassName: "bg-amber text-white",
    warningMessageClassName: "border border-border-warning-muted bg-amber-soft text-warning-ink shadow-none",
  },
  accent: {
    badgeClassName: "border border-[var(--color-border-accent-muted)] bg-accent text-white shadow-none",
    accentBarClassName: "bg-accent",
    iconShellClassName: "border border-[var(--color-border-accent-subtle)] bg-accent-soft text-accent-strong shadow-none",
    confirmButtonClassName: "border-[var(--color-border-accent-muted)] bg-accent text-white shadow-none hover:bg-accent-strong focus-visible:ring-accent/35",
    impactDotClassName: "bg-accent",
    warningIconClassName: "bg-accent text-white",
    warningMessageClassName: "border border-[var(--color-border-accent-subtle)] bg-accent-soft text-accent-strong shadow-none",
  },
  danger: {
    badgeClassName: "border border-rose-200 bg-rose-600 text-white shadow-none",
    accentBarClassName: "bg-rose-600",
    iconShellClassName: "border border-rose-200 bg-rose-50 text-rose-700 shadow-none",
    confirmButtonClassName: "border-rose-600 bg-rose-600 text-white shadow-none hover:bg-rose-700 focus-visible:ring-rose-500/35",
    impactDotClassName: "bg-rose-600",
    warningIconClassName: "bg-rose-600 text-white",
    warningMessageClassName: "border border-rose-200 bg-rose-50 text-rose-700 shadow-none",
  },
};

const summaryToneClassNames = {
  neutral: "border-line-soft bg-white text-text",
  success: "border-[var(--color-border-success-subtle)] bg-white text-[var(--color-shell-action-soft-ink)]",
  warning: "border-[var(--color-border-warning-subtle)] bg-white text-warning-ink",
};

function normalizeConfirmationFieldKey(value, fallbackKey) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  return normalizedValue || fallbackKey;
}

function buildCheckboxFieldEntries(confirmCheckbox, checkboxFields) {
  const sourceFields = [
    ...(confirmCheckbox ? [confirmCheckbox] : []),
    ...(Array.isArray(checkboxFields) ? checkboxFields.filter(Boolean) : []),
  ];

  return sourceFields.reduce((items, field, index) => {
    const label = typeof field?.label === "string" ? field.label.trim() : "";
    if (!label) {
      return items;
    }

    items.push({
      ...field,
      key: normalizeConfirmationFieldKey(field?.key, `confirmation-checkbox-${index}`),
      label,
    });
    return items;
  }, []);
}

function buildInitialCheckboxValues(fields) {
  return fields.reduce((values, field) => {
    values[field.key] = Boolean(field?.defaultChecked);
    return values;
  }, {});
}

function buildTextPromptFieldEntries(textPromptFields) {
  return (Array.isArray(textPromptFields) ? textPromptFields : []).reduce((items, field, index) => {
    const label = typeof field?.label === "string" ? field.label.trim() : "";
    if (!label) {
      return items;
    }

    items.push({
      ...field,
      key: normalizeConfirmationFieldKey(field?.key, `confirmation-text-prompt-${index}`),
      label,
    });
    return items;
  }, []);
}

function normalizeTextPromptFieldValue(field, value) {
  if (field?.type === "number") {
    return value == null ? "" : String(value);
  }
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function buildInitialTextPromptValues(fields) {
  return fields.reduce((values, field) => {
    values[field.key] = normalizeTextPromptFieldValue(field, field?.defaultValue);
    return values;
  }, {});
}

function isTextPromptNumberStepValid(field, numericValue) {
  const step = typeof field?.step === "number" && Number.isFinite(field.step)
    ? field.step
    : null;
  if (step == null || step <= 0) {
    return true;
  }

  const stepBase = typeof field?.min === "number" && Number.isFinite(field.min)
    ? field.min
    : 0;
  const stepRatio = (numericValue - stepBase) / step;
  return Math.abs(stepRatio - Math.round(stepRatio)) < 1e-9;
}

function isTextPromptFieldValid(field, value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  if (!normalizedValue) {
    return field?.required !== true;
  }

  if (field?.type === "number") {
    const numericValue = Number(normalizedValue);
    if (!Number.isFinite(numericValue)) {
      return false;
    }
    if (field?.positive === true && numericValue <= 0) {
      return false;
    }
    if (typeof field?.min === "number" && numericValue < field.min) {
      return false;
    }
    if (typeof field?.max === "number" && numericValue > field.max) {
      return false;
    }
    if (!isTextPromptNumberStepValid(field, numericValue)) {
      const initialNumericValue = Number(normalizeTextPromptFieldValue(field, field?.defaultValue).trim());
      if (!Number.isFinite(initialNumericValue) || Math.abs(numericValue - initialNumericValue) >= 1e-9) {
        return false;
      }
    }
  }

  if (field?.type === "time" && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(normalizedValue)) {
    return false;
  }

  if (
    typeof field?.minLength === "number"
    && normalizedValue.length < field.minLength
  ) {
    return false;
  }

  return true;
}

function canDisplayInlineSummary(item) {
  return item?.multiline !== true
    && item?.span !== "fill"
    && item?.span !== "wide"
    && ["string", "number"].includes(typeof item?.value)
    && String(item.value).length <= 24
    && !String(item.value).includes("\n");
}

function SummaryCard({ compact = false, grainCorners = false, inline = false, item, useCompactPairLayout = false, useHugLayout = false }) {
  const tone = summaryToneClassNames[item?.tone] || summaryToneClassNames.neutral;
  if (inline) {
    return (
      <div className={`inline-flex max-w-full items-baseline gap-1.5 rounded-[4px] border px-2.5 py-1.5 text-[0.82rem] leading-5 ${tone}`} data-confirmation-summary-inline>
        <span className="min-w-0 break-words text-muted-strong">{item?.label || "Résumé"} :</span>
        <strong className="shrink-0 whitespace-nowrap font-semibold tabular-nums">{item.value}</strong>
      </div>
    );
  }
  const isMultilineValue = item?.multiline === true;
  const isFillItem = item?.span === "fill";
  const isHugItem = item?.span === "hug" || useHugLayout;
  const isNowrapValue = item?.nowrap === true || isHugItem;
  const spanClassName = isHugItem
    ? "w-max max-w-full shrink-0"
    : isFillItem
      ? (useCompactPairLayout ? "min-w-0" : "sm:col-span-1 xl:col-span-3")
      : item?.span === "wide"
        ? "sm:col-span-1 xl:col-span-2"
        : useCompactPairLayout
          ? "shrink-0"
          : "";

  const valueClassName = isMultilineValue
    ? "mt-2 text-[0.88rem] font-medium leading-6 break-words text-current lg:text-[0.92rem]"
    : isHugItem
      ? (compact
        ? "mt-1.5 whitespace-nowrap text-[0.98rem] font-semibold leading-5 text-current lg:text-[1.02rem]"
        : "mt-2 whitespace-nowrap text-lg font-semibold leading-none text-current lg:text-[1.35rem]")
    : compact && useCompactPairLayout && isFillItem
      ? isNowrapValue
        ? "mt-1.5 min-w-0 overflow-x-auto text-[0.82rem] font-semibold leading-5 whitespace-nowrap text-current"
        : "mt-1.5 min-w-0 text-[0.84rem] font-semibold leading-5 break-all text-current"
      : compact && useCompactPairLayout
        ? "mt-1.5 max-w-full break-words text-[0.98rem] font-semibold leading-5 text-current"
        : compact
          ? isNowrapValue
            ? "mt-1.5 overflow-x-auto text-[0.88rem] font-semibold leading-5 whitespace-nowrap text-current lg:text-[0.92rem]"
            : "mt-1.5 text-[0.98rem] font-semibold leading-5 text-current lg:text-[1.02rem]"
          : isNowrapValue
            ? "mt-2 overflow-x-auto text-[0.98rem] font-semibold leading-none whitespace-nowrap text-current lg:text-[1.05rem]"
            : "mt-2 text-lg font-semibold leading-none text-current lg:text-[1.35rem]";

  return (
    <div className={[
      isHugItem ? "" : "min-w-0",
      grainCorners ? "rounded-[4px]" : compact ? "rounded-lg" : "rounded-xl",
      compact ? "border bg-white px-3 py-2 shadow-none" : "border bg-white px-3 py-3 shadow-none",
      tone,
      spanClassName,
    ].filter(Boolean).join(" ")}
    >
      <div className={compact
        ? "text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted-strong"
        : "text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-strong"}
      >
        {item?.label || "Resume"}
      </div>
      <div className={valueClassName}>
        {item?.value || "0"}
      </div>
    </div>
  );
}

export default function ConfirmationModal({
  children = null,
  cancelLabel = "Annuler",
  checkboxFields = [],
  confirmCheckbox = null,
  confirmLabel = "Confirmer",
  confirmDisabled = false,
  foreground = false,
  portalContainer = null,
  planningWriteGuard = false,
  contentDensity = "compact",
  cornerRadius = "grain",
  description = "",
  eyebrow = "Confirmation",
  impactItems = [],
  isOpen = true,
  isBusy = false,
  onCancel,
  onClose,
  onConfirm,
  onDismiss,
  panelSize = "default",
  secondaryAction = null,
  summaryItems = [],
  summaryDensity = "compact",
  summaryLayout = "grid",
  textPrompt = null,
  textPromptFields = [],
  title = "Confirmer cette action ?",
  tone = "warning",
  errorMessage = "",
  warningMessage = "",
  warningTitle = "",
  warningTone = "",
  warningVariant = "",
}) {
  const confirmButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const firstRequiredCheckboxRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const prerequisiteHintId = useId();
  const variant = toneVariants[tone] || toneVariants.warning;
  const warningBoxVariant = toneVariants[warningTone] || variant;
  const isReorderedProtectionWarning = warningVariant === REORDERED_PROTECTION_TONE;
  const resolvedCheckboxFields = buildCheckboxFieldEntries(confirmCheckbox, checkboxFields);
  const checkboxFieldSignature = resolvedCheckboxFields
    .map((field) => `${field.key}:${field.label}:${field.defaultChecked ? "1" : "0"}`)
    .join("|");
  const normalizedTextPrompt = textPrompt && typeof textPrompt?.label === "string" && textPrompt.label.trim()
    ? {
      ...textPrompt,
      key: normalizeConfirmationFieldKey(textPrompt?.key, "confirmation-text-prompt"),
      label: textPrompt.label.trim(),
    }
    : null;
  const resolvedTextPromptFields = buildTextPromptFieldEntries(textPromptFields);
  const textPromptFieldSignature = resolvedTextPromptFields
    .map((field) => [
      field.key,
      field.label,
      field.type || "text",
      field.defaultValue == null ? "" : String(field.defaultValue),
      field.required ? "1" : "0",
      field.positive ? "1" : "0",
      typeof field.min === "number" ? String(field.min) : "",
      typeof field.step === "number" ? String(field.step) : "",
    ].join(":"))
    .join("|");
  const [checkboxValues, setCheckboxValues] = useState(() => buildInitialCheckboxValues(resolvedCheckboxFields));
  const [textPromptValue, setTextPromptValue] = useState(typeof normalizedTextPrompt?.defaultValue === "string" ? normalizedTextPrompt.defaultValue : "");
  const [textPromptValues, setTextPromptValues] = useState(() => buildInitialTextPromptValues(resolvedTextPromptFields));
  const resolvedImpactItems = Array.isArray(impactItems) ? impactItems.filter(Boolean) : [];
  const impactGroups = resolvedImpactItems.length > 2
    ? [resolvedImpactItems.slice(0, -1), resolvedImpactItems.slice(-1)]
    : resolvedImpactItems.map(item => [item]);
  const resolvedSummaryItems = Array.isArray(summaryItems) ? summaryItems.filter(Boolean) : [];
  const hasFillSummaryItem = resolvedSummaryItems.some((item) => item?.span === "fill");
  const hasHugSummaryItem = resolvedSummaryItems.some((item) => item?.span === "hug");
  const useCompactPairSummaryLayout = summaryLayout !== "stack"
    && summaryLayout !== "wrap"
    && !hasHugSummaryItem
    && hasFillSummaryItem
    && resolvedSummaryItems.length === 2;
  const hasConfirmCheckbox = resolvedCheckboxFields.length > 0;
  const hasTextPrompt = Boolean(normalizedTextPrompt);
  const hasTextPromptFields = resolvedTextPromptFields.length > 0;
  const hasLegacyDialogPayload = hasTextPrompt || resolvedCheckboxFields.length > 1;
  const hasWarningMessage = typeof warningMessage === "string" && warningMessage.trim().length > 0;
  const hasErrorMessage = typeof errorMessage === "string" && errorMessage.trim().length > 0;
  const isCompactContent = contentDensity === "compact";
  const isNarrowPanel = panelSize === "narrow";
  const grainCorners = cornerRadius === "grain";
  const useInlineSummaryLayout = isCompactContent && summaryDensity === "compact"
    && ["grid", "wrap"].includes(summaryLayout)
    && resolvedSummaryItems.every(canDisplayInlineSummary);
  const hasWideContent = Boolean(children) || summaryLayout === "cohesion"
    || (resolvedSummaryItems.length > 0 && !useInlineSummaryLayout);
  const errorVariant = toneVariants.danger;
  const trimmedTextPromptValue = typeof textPromptValue === "string" ? textPromptValue.trim() : "";
  const isTimeTextPrompt = normalizedTextPrompt?.type === "time";
  const isCompactTextPrompt = normalizedTextPrompt?.width === "compact";
  const isSingleLineTextPrompt = normalizedTextPrompt?.type === "text"
    || normalizedTextPrompt?.type === "password"
    || normalizedTextPrompt?.type === "email";
  const isPasswordTextPrompt = normalizedTextPrompt?.type === "password";
  const hasInvalidTextPromptField = resolvedTextPromptFields.some(
    (field) => !isTextPromptFieldValid(field, textPromptValues[field.key]),
  );
  const singleLineTextPromptClassName = isPasswordTextPrompt
    ? `mt-2 h-10 w-full ${(isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-lg"} border border-[var(--color-border-accent-subtle)] bg-accent-soft px-3 text-[0.88rem] font-medium text-text shadow-none outline-hidden transition focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/15`
    : `mt-2 h-10 w-full ${(isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-lg"} border border-line-soft bg-surface px-3 text-[0.88rem] text-text shadow-none outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/15`;
  const firstUncheckedRequiredCheckboxKey = resolvedCheckboxFields.find(
    (field) => field?.required && !checkboxValues[field.key],
  )?.key || "";
  const hasRequiredCheckbox = resolvedCheckboxFields.some((field) => field?.required);
  const hasMissingRequiredCheckbox = Boolean(firstUncheckedRequiredCheckboxKey);
  const planningWriteMessage = usePlanningWriteActivity();
  const isConfirmDisabled = isBusy || confirmDisabled || (planningWriteGuard && Boolean(planningWriteMessage)) || hasMissingRequiredCheckbox
    || (hasTextPrompt && normalizedTextPrompt?.required && !trimmedTextPromptValue)
    || (
      hasTextPrompt
      && typeof normalizedTextPrompt?.minLength === "number"
      && trimmedTextPromptValue.length > 0
      && trimmedTextPromptValue.length < normalizedTextPrompt.minLength
    )
    || (hasTextPromptFields && hasInvalidTextPromptField);

  useEffect(() => {
    setCheckboxValues(buildInitialCheckboxValues(resolvedCheckboxFields));
  }, [checkboxFieldSignature, isOpen]);

  useEffect(() => {
    setTextPromptValue(typeof normalizedTextPrompt?.defaultValue === "string" ? normalizedTextPrompt.defaultValue : "");
  }, [isOpen, normalizedTextPrompt?.defaultValue, normalizedTextPrompt?.key, normalizedTextPrompt?.label]);

  useEffect(() => {
    setTextPromptValues(buildInitialTextPromptValues(resolvedTextPromptFields));
  }, [isOpen, textPromptFieldSignature]);

  const handleDismiss = () => {
    if (isBusy) return;
    if (typeof onDismiss === "function") {
      onDismiss();
      return;
    }
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    onClose?.();
  };

  const handleCancel = () => {
    if (isBusy) return;
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    handleDismiss();
  };

  const handleTimePromptClick = (event) => {
    if (normalizedTextPrompt?.openPickerOnClick !== true) {
      return;
    }
    try {
      event.currentTarget?.showPicker?.();
    } catch {
      // Some browsers expose showPicker but can still reject the native picker.
    }
  };

  const handleTextPromptFieldClick = (event, field) => {
    if (field?.openPickerOnClick !== true) {
      return;
    }
    try {
      event.currentTarget?.showPicker?.();
    } catch {
      // Some browsers expose showPicker but can still reject the native picker.
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.defaultPrevented || (!foreground && document.querySelector('[data-grain-dialog-foreground="true"]'))) return;
      if (event.key === "Tab") {
        if (handleBillingDialogKeyDown(event, { activeElement: document.activeElement, dialog: dialogRef.current })) event.stopPropagation();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        handleDismiss();
      }
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [isOpen, isBusy]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      if (firstUncheckedRequiredCheckboxKey) {
        firstRequiredCheckboxRef.current?.focus();
        return;
      }
      confirmButtonRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const panelSizeClassName = panelSize === "narrow" || (panelSize === "default" && !hasWideContent)
    ? "max-w-[min(36rem,calc(100vw-1.5rem))]"
    : panelSize === "compact" || (panelSize === "default" && !children && summaryLayout !== "cohesion")
      ? "max-w-[min(46rem,calc(100vw-1.5rem))]"
      : panelSize === "comfortable"
        ? "max-w-[min(50rem,calc(100vw-1.5rem))]"
        : "max-w-[min(56rem,calc(100vw-1.5rem))]";

  return createPortal(
    <div
      data-react-confirmation-modal="true"
      data-grain-dialog-foreground={foreground || undefined}
      style={foreground ? { zIndex: 10020 } : undefined}
      className="fixed inset-0 z-modal isolate font-sans"
      role="presentation"
    >
      <div
        className="absolute inset-0 animate-confirmation-modal-backdrop bg-slate-950/45 px-4 py-5 lg:px-6"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            handleDismiss();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            ref={dialogRef}
            aria-describedby={description ? descriptionId : undefined}
            aria-labelledby={titleId}
            aria-modal="true"
            aria-busy={isBusy}
            className={[
              "relative flex max-h-[calc(100dvh-2.5rem)] w-full animate-confirmation-modal-panel flex-col overflow-hidden border border-line-soft bg-surface text-text shadow-floating",
              (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-overlay",
              panelSizeClassName,
            ].join(" ")}
            role="alertdialog"
          >
            <div className={[isCompactContent ? "h-1" : "h-1.5", "w-full shrink-0", variant.accentBarClassName].join(" ")} />
            <div className={[
              "relative shrink-0 px-4",
              isCompactContent ? "pt-2.5" : "pt-3 sm:pt-3.5 lg:pt-3.5",
            ].join(" ")}>
              <div className="relative flex items-start justify-between gap-3">
                <div className={["flex min-w-0 flex-1 items-start md:pr-24", isCompactContent ? "gap-2.5" : "gap-3"].join(" ")}>
                  <div className={[
                    "flex shrink-0 items-center justify-center border",
                    (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-xl",
                    isCompactContent ? "h-8 w-8 text-sm" : "h-12 w-12 text-lg",
                    variant.iconShellClassName,
                  ].join(" ")}
                  >
                    <span aria-hidden="true">!</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className={[
                      "inline-flex px-2.5 py-0.75 text-[0.64rem] font-semibold uppercase tracking-[0.18em]",
                      (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-full",
                      variant.badgeClassName,
                    ].join(" ")}
                    >
                      {eyebrow}
                    </span>
                    <h2 id={titleId} className={[
                      "font-display font-semibold leading-tight tracking-[-0.02em] text-pretty text-text",
                      isCompactContent
                        ? "mt-1 text-lg sm:text-xl"
                        : "mt-2 text-[1.3rem] md:text-[clamp(1.38rem,1.7vw,1.62rem)]",
                    ].join(" ")}>
                      {renderInlineNoBreakIdentifiers(title)}
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  className={`shrink-0 ${(isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-full"} border border-line-soft bg-surface-muted px-3 py-1 text-[0.88rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 md:absolute md:right-0 md:top-0`}
                  onClick={handleDismiss}
                  disabled={isBusy}
                >
                  Fermer
                </button>
              </div>
            </div>

            <div className={[
              "relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-4",
              isCompactContent ? "pt-1.5" : "pt-2",
            ].join(" ")}>
              {description ? (
                <p id={descriptionId} className="w-full whitespace-pre-line text-pretty text-[0.88rem] leading-5 text-muted-strong lg:text-[0.9rem]">
                  {description}
                </p>
              ) : null}

              {hasErrorMessage ? (
                <div className={[
                  "mt-2 flex items-center gap-2 px-3 py-1.75",
                  grainCorners ? "rounded-[4px]" : "rounded-lg",
                  errorVariant.warningMessageClassName,
                ].join(" ")}
                role="alert"
                >
                  <span className={[
                    "inline-flex h-4 w-4 shrink-0 items-center justify-center text-[0.68rem] font-black",
                    grainCorners ? "rounded-[4px]" : "rounded-full",
                    errorVariant.warningIconClassName,
                  ].join(" ")}>
                    !
                  </span>
                  <p className="m-0 min-w-0 text-[0.82rem] font-medium leading-5 lg:text-[0.85rem]">
                    {errorMessage}
                  </p>
                </div>
              ) : null}

              {hasWarningMessage && !hasErrorMessage ? (
                <div className={[
                  "mt-2 flex gap-2 px-3 py-1.75",
                  warningTitle ? "items-start" : "items-center",
                  grainCorners ? "rounded-[4px]" : "rounded-lg",
                  isReorderedProtectionWarning
                    ? "border border-[rgba(109,40,217,0.2)] bg-[rgba(245,243,255,0.98)] shadow-none"
                    : warningBoxVariant.warningMessageClassName,
                ].join(" ")}
                style={isReorderedProtectionWarning ? { color: REORDERED_PROTECTION_ACCENT_COLOR } : undefined}
                >
                  {isReorderedProtectionWarning ? (
                    <ShieldCheck aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={2.75} />
                  ) : (
                    <span className={[
                      "inline-flex h-4 w-4 shrink-0 items-center justify-center text-[0.68rem] font-black",
                      warningTitle ? "mt-0.5" : "",
                      grainCorners ? "rounded-[4px]" : "rounded-full",
                      warningBoxVariant.warningIconClassName,
                    ].join(" ")}>
                      !
                    </span>
                  )}
                  <p className={`m-0 min-w-0 text-[0.82rem] font-medium leading-5 lg:text-[0.85rem]${warningTitle ? " whitespace-pre-line" : ""}`}>
                    {warningTitle ? <strong className="mb-0.5 block text-[0.9rem] font-semibold">{warningTitle}</strong> : null}
                    {warningMessage}
                  </p>
                </div>
              ) : null}

              {hasConfirmCheckbox ? (
                <div className="mt-2 grid gap-2">
                  {resolvedCheckboxFields.map((field) => {
                    const checkboxId = `${titleId}-${field.key}`;
                    const isRequired = field?.required === true;
                    const isChecked = Boolean(checkboxValues[field.key]);

                    return (
                    <label
                      htmlFor={checkboxId}
                      key={field.key}
                      className={[
                        "flex cursor-pointer items-start gap-2.5 border bg-white px-3 py-2 text-text shadow-none transition",
                        grainCorners ? "rounded-[4px]" : "rounded-lg",
                        isRequired && !isChecked
                          ? "border-rose-300 hover:border-rose-500 hover:bg-rose-50/40"
                          : "border-border-warning-muted hover:border-amber",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        aria-describedby={isRequired ? prerequisiteHintId : undefined}
                        checked={isChecked}
                        id={checkboxId}
                        onChange={(event) => {
                          setCheckboxValues((currentValues) => ({
                            ...currentValues,
                            [field.key]: event.target.checked,
                          }));
                        }}
                        ref={field.key === firstUncheckedRequiredCheckboxKey ? firstRequiredCheckboxRef : undefined}
                        required={isRequired}
                        className={`mt-0.5 h-5 w-5 shrink-0 cursor-pointer ${grainCorners ? "rounded-[4px]" : "rounded"} border-border-warning-muted text-amber focus:ring-2 focus:ring-amber/35`}
                      />
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2 text-[0.84rem] font-semibold leading-5 text-text lg:text-[0.87rem]">
                          {field.label}
                          {isRequired ? (
                            <span className={[
                              "px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                              grainCorners ? "rounded-[4px]" : "rounded-full",
                              isChecked ? "bg-accent-soft text-accent-strong" : "bg-rose-100 text-rose-700",
                            ].join(" ")}
                            >
                              {isChecked ? "Cochée" : "À cocher"}
                            </span>
                          ) : null}
                        </span>
                        {typeof field?.description === "string" && field.description.trim() ? (
                          <span className="mt-0.5 block text-[0.75rem] leading-4.5 text-muted-strong lg:text-[0.78rem]">
                            {field.description}
                          </span>
                        ) : null}
                      </span>
                    </label>
                    );
                  })}
                </div>
              ) : null}

              {hasTextPrompt ? (
                <label className={[
                  "mt-2 block border px-3 py-2.5 text-text shadow-none",
                  (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-lg",
                  isPasswordTextPrompt
                    ? "border-[var(--color-border-accent-subtle)] bg-accent-soft/35"
                    : "border-border-warning-muted bg-white",
                ].join(" ")}
                >
                  <span className="block text-[0.84rem] font-semibold leading-5 text-text lg:text-[0.87rem]">
                    {normalizedTextPrompt.label}
                  </span>
                  {typeof normalizedTextPrompt?.description === "string" && normalizedTextPrompt.description.trim() ? (
                    <span className="mt-0.75 block text-[0.75rem] leading-4.5 text-muted-strong lg:text-[0.78rem]">
                      {normalizedTextPrompt.description}
                    </span>
                  ) : null}
                  {isTimeTextPrompt ? (
                    <input
                      type="time"
                      value={textPromptValue}
                      onChange={(event) => setTextPromptValue(event.target.value)}
                      onClick={handleTimePromptClick}
                      step={typeof normalizedTextPrompt?.step === "number" ? normalizedTextPrompt.step : 60}
                      inputMode="numeric"
                      className={[
                        "mt-2 h-9 border border-line-soft bg-surface px-3 text-[0.88rem] text-text shadow-none outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/15",
                        isCompactTextPrompt ? "w-full sm:w-[13rem]" : "w-full",
                        (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-lg",
                      ].join(" ")}
                    />
                  ) : isSingleLineTextPrompt ? (
                    <input
                      type={normalizedTextPrompt.type}
                      value={textPromptValue}
                      onChange={(event) => setTextPromptValue(event.target.value)}
                      autoComplete={typeof normalizedTextPrompt?.autoComplete === "string" ? normalizedTextPrompt.autoComplete : undefined}
                      minLength={typeof normalizedTextPrompt?.minLength === "number" ? normalizedTextPrompt.minLength : undefined}
                      maxLength={typeof normalizedTextPrompt?.maxLength === "number" ? normalizedTextPrompt.maxLength : undefined}
                      placeholder={typeof normalizedTextPrompt?.placeholder === "string" ? normalizedTextPrompt.placeholder : undefined}
                      inputMode={normalizedTextPrompt?.type === "email" ? "email" : undefined}
                      className={singleLineTextPromptClassName}
                    />
                  ) : (
                    <textarea
                      value={textPromptValue}
                      onChange={(event) => setTextPromptValue(event.target.value)}
                      rows={normalizedTextPrompt?.rows || 3}
                      minLength={typeof normalizedTextPrompt?.minLength === "number" ? normalizedTextPrompt.minLength : undefined}
                      maxLength={typeof normalizedTextPrompt?.maxLength === "number" ? normalizedTextPrompt.maxLength : undefined}
                      placeholder={typeof normalizedTextPrompt?.placeholder === "string" ? normalizedTextPrompt.placeholder : undefined}
                      className={`mt-2 min-h-22 w-full ${(isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-lg"} border border-line-soft bg-surface px-3 py-2 text-[0.88rem] leading-5 text-text shadow-none outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/15`}
                    />
                  )}
                </label>
              ) : null}

              {hasTextPromptFields ? (
                <div
                  className={`mt-2 grid gap-2 ${resolvedTextPromptFields.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
                  data-confirmation-text-prompt-fields
                >
                  {resolvedTextPromptFields.map((field) => {
                    const fieldId = `${titleId}-${field.key}`;
                    const fieldType = typeof field?.type === "string" && field.type.trim()
                      ? field.type
                      : "text";
                    const fieldValue = textPromptValues[field.key] ?? "";
                    const isFieldInvalid = !isTextPromptFieldValid(field, fieldValue);

                    // Champ grisé (disabled) : sa valeur actuelle reste, la bulle du cadre (title) dit pourquoi.
                    return (
                      <label
                        className="block rounded-[4px] border border-line-soft bg-white px-3 py-2.5 text-text shadow-none"
                        htmlFor={fieldId}
                        key={field.key}
                        title={field?.disabled === true ? field.title : undefined}
                      >
                        <span className="block text-[0.84rem] font-semibold leading-5 text-text lg:text-[0.87rem]">
                          {field.label}
                        </span>
                        {typeof field?.description === "string" && field.description.trim() ? (
                          <span className="mt-0.75 block text-[0.75rem] leading-4.5 text-muted-strong lg:text-[0.78rem]">
                            {field.description}
                          </span>
                        ) : null}
                        <input
                          aria-invalid={isFieldInvalid ? "true" : undefined}
                          autoComplete={typeof field?.autoComplete === "string" ? field.autoComplete : undefined}
                          disabled={field?.disabled === true}
                          id={fieldId}
                          inputMode={fieldType === "number" ? "decimal" : fieldType === "time" ? "numeric" : undefined}
                          max={fieldType === "number" && typeof field?.max === "number" ? field.max : undefined}
                          maxLength={typeof field?.maxLength === "number" ? field.maxLength : undefined}
                          min={fieldType === "number" && typeof field?.min === "number" ? field.min : undefined}
                          minLength={typeof field?.minLength === "number" ? field.minLength : undefined}
                          name={field.key}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setTextPromptValues((currentValues) => ({
                              ...currentValues,
                              [field.key]: nextValue,
                            }));
                          }}
                          onClick={(event) => handleTextPromptFieldClick(event, field)}
                          placeholder={typeof field?.placeholder === "string" ? field.placeholder : undefined}
                          required={field?.required === true}
                          step={typeof field?.step === "number" ? field.step : undefined}
                          type={fieldType}
                          value={fieldValue}
                          className="mt-2 h-9 w-full rounded-[4px] border border-line-soft bg-surface px-3 text-[0.88rem] text-text shadow-none outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-muted"
                        />
                      </label>
                    );
                  })}
                </div>
              ) : null}

              {resolvedSummaryItems.length ? (
                <div className={[
                  "relative gap-2",
                  isCompactContent ? "mt-2.5" : "mt-3.5",
                  useInlineSummaryLayout ? "flex flex-wrap"
                    : summaryLayout === "stack"
                    ? "grid grid-cols-1"
                    : summaryLayout === "three"
                      ? "grid grid-cols-3"
                    : summaryLayout === "cohesion"
                      ? "grid grid-cols-2 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,0.7fr)_minmax(0,1.5fr)_minmax(0,0.85fr)_minmax(0,0.85fr)]"
                    : summaryLayout === "wrap" || hasHugSummaryItem
                      ? "flex flex-wrap"
                    : useCompactPairSummaryLayout
                      ? "grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)]"
                      : "grid sm:grid-cols-2 xl:grid-cols-4",
                ].join(" ")}
                >
                  {resolvedSummaryItems.map((item) => (
                    <SummaryCard
                      compact={summaryDensity === "compact"}
                      grainCorners={grainCorners}
                      inline={useInlineSummaryLayout}
                      key={`${item.label}-${item.value}`}
                      item={item}
                      useCompactPairLayout={useCompactPairSummaryLayout}
                      useHugLayout={summaryLayout === "wrap" || hasHugSummaryItem}
                    />
                  ))}
                </div>
              ) : null}

              {resolvedImpactItems.length ? (
                <ul className={`grid gap-1.5 ${isCompactContent ? "mt-2.5" : "mt-3.5"}`} aria-label="Conséquences de l’action">
                  {impactGroups.map((items, index) => (
                    <li key={index} className="flex items-start gap-2 text-[0.82rem] leading-5 text-muted-strong">
                      <span aria-hidden="true" className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-none ${variant.impactDotClassName}`} />
                      <span className="min-w-0 break-words text-pretty">{items.map((item, itemIndex) => (
                        <React.Fragment key={itemIndex}>{itemIndex > 0 ? " " : null}{item}</React.Fragment>
                      ))}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {children}
              {hasRequiredCheckbox ? (
                <p
                  aria-live="polite"
                  className={[
                    "relative mt-3 text-right text-[0.78rem] font-semibold leading-5",
                    hasMissingRequiredCheckbox ? "text-rose-700" : "text-accent-strong",
                  ].join(" ")}
                  id={prerequisiteHintId}
                  role="status"
                >
                  {hasMissingRequiredCheckbox
                    ? `Cochez la confirmation ci-dessus pour activer « ${confirmLabel} ».`
                    : `Confirmation cochée : « ${confirmLabel} » est maintenant activé.`}
                </p>
              ) : null}
            </div>

            <div className={[
              "relative shrink-0 px-4",
              isCompactContent ? "pb-2.5 pt-2.5" : "pb-3.5 pt-3 sm:pb-4 lg:pb-4",
              hasRequiredCheckbox ? "pt-2" : "",
            ].join(" ")}>
              {planningWriteGuard ? <PlanningWriteActivityNotice /> : null}
              <div className="relative flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {cancelLabel ? <button
                  type="button"
                  className={`${(isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-full"} border border-line-soft bg-white px-4 py-2 text-[0.9rem] font-semibold text-muted-strong shadow-none transition hover:border-accent hover:bg-surface-muted hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2`}
                  onClick={handleCancel}
                  disabled={isBusy}
                >
                  {cancelLabel}
                </button> : null}
                {secondaryAction ? <button
                  type="button"
                  className="rounded-[4px] border border-slate-300 bg-white px-4 py-2 text-[0.9rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                  onClick={secondaryAction.onClick}
                  disabled={isBusy}
                >
                  {secondaryAction.label}
                </button> : null}
                <button
                  ref={confirmButtonRef}
                  type="button"
                  aria-describedby={hasRequiredCheckbox ? prerequisiteHintId : undefined}
                  className={[
                    "border px-4 py-2 text-[0.9rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2",
                    (isNarrowPanel || grainCorners) ? "rounded-[4px]" : "rounded-full",
                    isConfirmDisabled ? `cursor-not-allowed ${grainCorners ? "disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none" : "opacity-60"}` : "",
                    variant.confirmButtonClassName,
                  ].join(" ")}
                  disabled={isConfirmDisabled}
                  onClick={() => {
                    const primaryCheckboxKey = resolvedCheckboxFields[0]?.key || "";
                    const primaryCheckboxValue = primaryCheckboxKey ? Boolean(checkboxValues[primaryCheckboxKey]) : false;

                    if (hasTextPrompt || hasTextPromptFields || resolvedCheckboxFields.length > 1) {
                      onConfirm?.({
                        checkboxValues: { ...checkboxValues },
                        confirmCheckboxValue: primaryCheckboxValue,
                        ...(hasLegacyDialogPayload
                          ? {
                              textPromptKey: normalizedTextPrompt?.key || "",
                              textPromptValue: trimmedTextPromptValue,
                            }
                          : {}),
                        ...(hasTextPromptFields
                          ? {
                              textPromptValues: Object.fromEntries(
                                Object.entries(textPromptValues).map(([key, value]) => [
                                  key,
                                  typeof value === "string" ? value.trim() : value,
                                ]),
                              ),
                            }
                          : {}),
                      });
                      return;
                    }

                    onConfirm?.(primaryCheckboxValue);
                  }}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    portalContainer || getReactPortalRoot(),
  );
}
